import {Component, DestroyRef, OnInit, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';
import {NgxChartsModule, Color, ScaleType} from '@swimlane/ngx-charts';
import {PureOriginalExportTrendStats, TrendGranularity} from '../../models/dashboard-statistics.model';
import {DashboardStatisticsService} from '../../services/dashboard-statistics.service';
import {DashboardPeriodService, PeriodMode} from '../../../dashboard/services/dashboard-period.service';

@Component({
  selector: 'app-po-export-trends',
  standalone: true,
  imports: [CommonModule, NgxChartsModule],
  templateUrl: './po-export-trends.component.html',
  styleUrls: ['./po-export-trends.component.css']
})
export class PoExportTrendsComponent implements OnInit {

  private readonly statsService = inject(DashboardStatisticsService);
  private readonly periodService = inject(DashboardPeriodService);
  private readonly destroyRef = inject(DestroyRef);

  // This card has its own Day/Week/Month filter that drives its own date range,
  // independent of the global date selector at the top of the dashboard.
  granularity = signal<TrendGranularity>('DAY');
  isLoading = signal<boolean>(true);
  chartData: any[] = [];

  colorScheme: Color = {
    name: 'custom',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#7770CD', '#E8A838', '#5AA155']
  };

  formatXAxisTick = (val: string): string => {
    if (!val) return val;
    const parts = val.split('-');
    if (parts.length >= 3) {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthIdx = parseInt(parts[1], 10) - 1;
      return `${months[monthIdx]} ${parseInt(parts[2], 10)}`;
    }
    return val;
  };

  ngOnInit(): void {
    // Load once with the card's own range; global selector changes are ignored.
    this.loadData();
  }

  onGranularityChange(g: TrendGranularity): void {
    this.granularity.set(g);
    this.loadData();
  }

  /** Local date range derived from this card's own Day/Week/Month switch. */
  private localRange(): {start: string; end: string} {
    const g = this.granularity();
    const mode: Exclude<PeriodMode, 'CUSTOM'> = g === 'DAY' ? 'TODAY' : g;
    return this.periodService.presetRange(mode);
  }

  private loadData(): void {
    const {start, end} = this.localRange();
    this.isLoading.set(true);
    this.statsService.getPureOriginalExportTrends(start, end, this.granularity())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: trends => {
          this.chartData = this.transformData(trends);
          this.isLoading.set(false);
        },
        error: () => this.isLoading.set(false)
      });
  }

  private transformData(trends: PureOriginalExportTrendStats[]): any[] {
    if (!trends || !trends.length) return [];

    return [
      {
        name: 'Main DB',
        series: trends.map(t => ({name: t.period, value: t.exportedMainDatabase}))
      },
      {
        name: 'WinOLS5',
        series: trends.map(t => ({name: t.period, value: t.exportedWinOls5Database}))
      },
      {
        name: 'Reseller',
        series: trends.map(t => ({name: t.period, value: t.exportedReseller}))
      }
    ];
  }
}
